import { SparklesIcon } from '@/components/Icons'
import ActionHintBubble from '@/components/ActionHintBubble'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useAnimationReplay } from '@/hooks/useAnimationReplay'
import { MEETING_CONTROL_PILL, MEETING_CONTROL_PILL_LABEL } from '../meetingControlPill'

type CopilotFooterButtonProps = {
  active: boolean
  unreadCount: number
  /** Bob a few times to be noticed, for a reader who has never opened the panel (BRAI-685). */
  nudge: boolean
  /** Say what the button is for, above it, for a few seconds (BRAI-685). */
  hint: boolean
  onClick: () => void
}

export default function CopilotFooterButton({
  active,
  unreadCount,
  nudge,
  hint,
  onClick,
}: CopilotFooterButtonProps) {
  const { t } = useUiStrings()
  // Replayed on the unread count, so each suggestion that lands behind a shut
  // panel gets its own bob. Left as a steady class it would have played once,
  // on arrival, and never again however much came in after it.
  const bob = useAnimationReplay(nudge, unreadCount)

  return (
    // Wears the control bar's pill, like every named action beside it, so the
    // row still reads as one set — but filled rather than grey (BRAI-657).
    // Copilot used to be the only AI feature in this row dressed as a utility,
    // sitting next to the blue "AI Live" chip that outshone it.
    //
    // The wrapper stays even though the label moved inside the button: the hint
    // bubble above it is positioned against this box.
    <div className="relative flex flex-col items-center">
      {/* Keyed on the count so a new suggestion remounts the bubble and starts
          its few seconds again — the same sentence left up from last time
          would say nothing about the one that just arrived. */}
      <ActionHintBubble
        key={unreadCount}
        id="copilot-action-hint"
        text={unreadCount > 0 ? t.copilotNewItemsHint : t.copilotActionHint}
        show={hint}
      />
      <button
        type="button"
        onClick={onClick}
        data-testid="copilot-footer-button"
        aria-describedby={hint ? 'copilot-action-hint' : undefined}
        className={[
          MEETING_CONTROL_PILL,
          'relative',
          bob ? 'motion-safe:animate-nudge-bounce' : '',
          // Filled while open, tonal while shut — pressed is the heavier of the
          // pair, as a toggle is read everywhere else. The halo is on top of
          // the fill because the "AI Live" chip beside it is the same accent
          // blue, so fill alone left the pressed state looking like part of
          // it. `ring-offset-*` has to name the bar's own background, which is
          // what the gap is cut out of.
          //
          // Shut is tonal rather than the grey it used to be (BRAI-657): the
          // accent fill and border keep it in the same family as the "AI Live"
          // chip beside it instead of among the utilities. Two shades of one
          // fill were tried for this pair first and could not be told apart at
          // 44px, which is why the difference is weight and not darkness.
          active
            ? 'bg-blue-600 text-white ring-2 ring-blue-300 ring-offset-2 ring-offset-white hover:bg-blue-700 dark:bg-[var(--accent)] dark:ring-[var(--accent)] dark:ring-offset-[var(--bg-tertiary)] dark:hover:bg-[var(--accent-hover)]'
            : 'bg-blue-50 text-blue-700 ring-1 ring-blue-200 hover:bg-blue-100 dark:bg-[var(--accent-light)] dark:text-[var(--accent)] dark:ring-[var(--border-color)]',
        ].join(' ')}
        aria-pressed={active}
        title={t.copilotLabel}
        aria-label={t.copilotLabel}
      >
        {/* Outside the button, not on it: a ring drawn inside a filled square
            has the fill on both sides of it and reads as a seam. Decorative —
            the count beside it is what carries the news to a screen reader. */}
        {unreadCount > 0 ? (
          <span
            aria-hidden="true"
            data-testid="copilot-unread-ring"
            className="pointer-events-none absolute -inset-1 rounded-2xl ring-2 ring-blue-400/70 motion-safe:animate-pulse-ring dark:ring-[var(--accent)]"
          />
        ) : null}
        <SparklesIcon className="h-4 w-4 shrink-0" />
        {/* The control bar is a single non-wrapping row, so the label collapses
            to the icon below `sm`; `aria-label` keeps the accessible name. */}
        <span className={MEETING_CONTROL_PILL_LABEL}>{t.copilotLabel}</span>
        {unreadCount > 0 ? (
          <span
            // Solid on the tonal button, which is the only one it is ever seen
            // on: opening the panel clears the count, so a badge and the
            // filled state do not co-occur. It was briefly the same blue as a
            // filled button and dissolved into it — hence a colour chosen
            // against the state it actually appears over, not against both.
            className="absolute -right-1 -top-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white ring-2 ring-white dark:bg-[var(--accent)] dark:ring-[var(--bg-secondary)]"
            aria-label={`${unreadCount} unread`}
          >
            {unreadCount}
          </span>
        ) : null}
      </button>
    </div>
  )
}
